import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GPDP_LOGO } from "@/lib/constants";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, User, MapPin, Phone, Calendar, Users as UsersIcon, Church, Heart, CheckCircle2, HeartHandshake, Plus, Trash2, Camera as CameraIcon, X, Upload } from "lucide-react";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return d; }
};

const CONDITIONS = [
  { key: "SEHAT", label: "Sehat", tone: "success" },
  { key: "SAKIT", label: "Sakit", tone: "warning" },
  { key: "DUKA", label: "Duka", tone: "destructive" },
  { key: "BAHAGIA", label: "Bahagia", tone: "info" },
  { key: "LAINNYA", label: "Lainnya", tone: "muted" },
];
const condMeta = (k) => CONDITIONS.find((c) => c.key === k) || { label: k, tone: "muted" };

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function KartuAnggota() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [openVisit, setOpenVisit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ visit_date: todayISO(), condition: "SEHAT", notes: "", prayer_notes: "" });
  const printRef = useRef();

  const canCreateVisit = ["SUPER_ADMIN", "ADMIN_KLASIS", "SEKRETARIS_KLASIS", "GEMBALA_JEMAAT", "SEKRETARIS_JEMAAT"].includes(user?.role);
  const canDeleteAny = ["SUPER_ADMIN", "ADMIN_KLASIS", "SEKRETARIS_KLASIS"].includes(user?.role);
  const canEditPhoto = canCreateVisit; // same roles that can manage member data

  const uploadAnggotaPhoto = async (file) => {
    if (!file) return;
    if (file.size > 1_800_000) { toast.error("Ukuran foto maksimal 1.5MB"); return; }
    try {
      const b64 = await fileToBase64(file);
      await api.patch(`/anggota/${id}/photo`, { photo_base64: b64, photo_url: null });
      toast.success("Foto anggota diperbarui");
      // Refresh kartu
      const { data } = await api.get(`/anggota/${id}/kartu`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload foto");
    }
  };

  const removeAnggotaPhoto = async () => {
    if (!window.confirm("Hapus foto anggota?")) return;
    try {
      await api.patch(`/anggota/${id}/photo`, { photo_base64: "", photo_url: "" });
      toast.success("Foto dihapus");
      const { data } = await api.get(`/anggota/${id}/kartu`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/anggota/${id}/kartu`);
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Tidak dapat memuat kartu");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const loadVisits = async () => {
    setVisitsLoading(true);
    try {
      const { data } = await api.get(`/pastoral-visits?anggota_id=${id}`);
      setVisits(data || []);
    } catch (e) {
      // silent — user may not have access
      setVisits([]);
    } finally { setVisitsLoading(false); }
  };

  useEffect(() => { if (user && id) loadVisits(); }, [id, user?.id]);

  const submitVisit = async () => {
    if (!form.visit_date) { toast.error("Tanggal kunjungan wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/pastoral-visits", {
        anggota_id: id,
        visit_date: form.visit_date,
        condition: form.condition,
        notes: form.notes || null,
        prayer_notes: form.prayer_notes || null,
        photos_base64: [],
      });
      toast.success("Kunjungan pastoral tercatat");
      setOpenVisit(false);
      setForm({ visit_date: todayISO(), condition: "SEHAT", notes: "", prayer_notes: "" });
      loadVisits();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mencatat kunjungan");
    } finally { setSaving(false); }
  };

  const removeVisit = async (visitId) => {
    if (!window.confirm("Hapus catatan kunjungan ini?")) return;
    try {
      await api.delete(`/pastoral-visits/${visitId}`);
      toast.success("Kunjungan dihapus");
      loadVisits();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menghapus");
    }
  };

  const doPrint = () => window.print();

  const verifyUrl = useMemo(() => {
    if (!data?.anggota?.member_number) return "";
    return `${window.location.origin}/verify/${encodeURIComponent(data.anggota.member_number)}`;
  }, [data]);

  if (loading) {
    return <AppShell><div className="p-12 text-center text-sm text-muted-foreground">Memuat kartu…</div></AppShell>;
  }
  if (!data) {
    return <AppShell><div className="p-12 text-center text-sm text-muted-foreground">Data tidak ditemukan.</div></AppShell>;
  }

  const a = data.anggota;
  const ch = data.church;
  const initials = (a.name || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();

  return (
    <AppShell>
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Link to="/anggota/import" className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="link-back-anggota">
          <ArrowLeft className="h-3 w-3" /> Kembali ke daftar anggota
        </Link>
        <Button size="sm" onClick={doPrint} className="rounded-full" data-testid="btn-print-kartu">
          <Printer className="h-3.5 w-3.5 mr-1.5" /> Cetak / PDF
        </Button>
      </div>

      <div ref={printRef} className="max-w-3xl mx-auto">
        {/* Front side */}
        <Card className="overflow-hidden shadow-lg print:shadow-none print:border-2 print:border-primary">
          <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-6">
            <div className="absolute inset-0 papua-motif opacity-30" />
            <div className="relative flex items-start gap-4">
              {/* Photo / initials */}
              <div className="relative shrink-0">
                {(a.photo_url || a.photo_base64) ? (
                  <img
                    src={a.photo_url || a.photo_base64}
                    alt={a.name}
                    className="h-20 w-20 rounded-lg object-cover border-2 border-secondary shadow-md"
                    data-testid="img-anggota-photo"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-white/95 text-primary flex items-center justify-center font-heading text-2xl font-bold border-2 border-secondary shadow-md">
                    {initials}
                  </div>
                )}
                {canEditPhoto && (
                  <label className="absolute -bottom-1 -right-1 bg-secondary text-secondary-foreground rounded-full p-1.5 cursor-pointer shadow-sm hover:scale-110 transition-transform print:hidden" title="Upload foto dari drive/laptop" data-testid="label-upload-anggota-photo">
                    <CameraIcon className="h-3 w-3" />
                    <input type="file" accept="image/*" hidden data-testid="input-upload-anggota-photo"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAnggotaPhoto(f); e.target.value = ""; }} />
                  </label>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-secondary font-bold">Kartu Anggota Jemaat</div>
                <div className="font-heading font-bold text-xl leading-tight mt-1 truncate">{a.name}</div>
                <div className="text-xs mt-1 opacity-90 flex items-center gap-1"><Church className="h-3 w-3" /> {ch?.name || "—"}</div>
                <div className="text-[0.7rem] opacity-80 mt-2 font-mono">No: {a.member_number || "—"}</div>
              </div>
              {verifyUrl && (
                <div className="bg-white p-1.5 rounded-md shrink-0" data-testid="qr-anggota">
                  <QRCodeSVG value={verifyUrl} size={72} level="M" bgColor="#FFFFFF" fgColor="#17365D" />
                </div>
              )}
            </div>
          </div>

          <CardContent className="relative p-6 space-y-4" data-testid="kartu-anggota-body">
            <div
              className="kartu-watermark"
              style={{ "--kartu-watermark-image": `url(${GPDP_LOGO})` }}
              aria-hidden="true"
              data-testid="kartu-anggota-watermark"
            />
            <div className="relative grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold">Tempat / Tgl Lahir</div>
                <div className="mt-0.5 font-medium">
                  {a.birth_place || "—"}{a.birth_place ? ", " : ""}{fmtDate(a.birth_date)}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold">Jenis Kelamin</div>
                <div className="mt-0.5 font-medium">{a.gender === "L" ? "Laki-laki" : a.gender === "P" ? "Perempuan" : "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold flex items-center gap-1"><MapPin className="h-3 w-3" /> Alamat</div>
                <div className="mt-0.5 text-sm">{a.address || "—"}</div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold flex items-center gap-1"><Phone className="h-3 w-3" /> No HP</div>
                <div className="mt-0.5 font-mono text-sm">{a.phone || "—"}</div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold flex items-center gap-1"><UsersIcon className="h-3 w-3" /> Peran Keluarga</div>
                <div className="mt-0.5 text-sm">{a.family_role ? a.family_role.replace(/_/g, " ") : "—"}</div>
              </div>
            </div>

            {/* Sakramental */}
            <div className="border-t border-border pt-4">
              <div className="text-xs font-semibold text-primary mb-2 flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> Data Sakramental</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Baptis</div>
                  <div className="mt-0.5">{fmtDate(a.baptis_date)} {a.baptis_date && <CheckCircle2 className="h-3 w-3 inline text-emerald-600" />}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Sidi</div>
                  <div className="mt-0.5">{fmtDate(a.sidi_date)} {a.sidi_date && <CheckCircle2 className="h-3 w-3 inline text-emerald-600" />}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Nikah Gerejawi</div>
                  <div className="mt-0.5">{fmtDate(a.nikah_gerejawi_date)} {a.nikah_gerejawi_date && <CheckCircle2 className="h-3 w-3 inline text-emerald-600" />}</div>
                </div>
              </div>
            </div>

            {/* Pelayanan */}
            <div className="border-t border-border pt-4">
              <div className="text-xs font-semibold text-primary mb-2">Pelayanan</div>
              <div className="flex flex-wrap gap-2 text-xs">
                {a.wadah && <StatusBadge status="info" label={`Wadah ${a.wadah}`} />}
                {a.komisi && <StatusBadge status="warning" label={a.komisi} />}
                {!a.wadah && !a.komisi && <span className="text-muted-foreground">—</span>}
              </div>
            </div>

            {/* Barcode-like member_number strip */}
            <div className="border-t border-border pt-3 mt-2 flex items-center justify-between">
              <div className="text-[0.65rem] text-muted-foreground">
                <div>Diterbitkan: {fmtDate(a.created_at)}</div>
                <div>Ditandatangani: {ch?.short_name ? `Gembala ${ch.short_name}` : "Gembala Jemaat"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[0.7rem] tracking-wider text-primary">{a.member_number}</div>
                <div className="text-[0.6rem] text-muted-foreground">Scan QR untuk verifikasi</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional info card (visible in print) */}
        <div className="mt-4 text-[0.65rem] text-muted-foreground text-center print:mt-2">
          Kartu ini adalah identitas resmi anggota jemaat {ch?.name} • GPDP Klasis Mimika<br/>
          Verifikasi kartu: <span className="font-mono">{verifyUrl}</span>
        </div>

        {/* Kunjungan Pastoral (hidden in print) */}
        <div className="mt-8 print:hidden" data-testid="pastoral-section">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Log Pelayanan</div>
                  <div className="font-heading font-bold text-lg mt-1 flex items-center gap-2">
                    <HeartHandshake className="h-5 w-5 text-secondary" /> Kunjungan Pastoral
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                    Catatan pelayanan Gembala/Sekretaris Jemaat kepada anggota — kondisi, doa, dan tindak lanjut.
                  </p>
                </div>
                {canCreateVisit && (
                  <Dialog open={openVisit} onOpenChange={setOpenVisit}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-full" data-testid="btn-new-visit">
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Catat Kunjungan
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Kunjungan Pastoral untuk {a.name}</DialogTitle>
                        <DialogDescription>
                          Isi tanggal, kondisi anggota, dan catatan pelayanan. Data hanya bisa dilihat oleh internal jemaat & klasis.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Tanggal Kunjungan</Label>
                            <Input
                              type="date"
                              value={form.visit_date}
                              onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
                              data-testid="input-visit-date"
                            />
                          </div>
                          <div>
                            <Label>Kondisi Anggota</Label>
                            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                              <SelectTrigger data-testid="select-visit-condition"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CONDITIONS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label>Catatan Kunjungan</Label>
                          <Textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Kondisi keluarga, kebutuhan, tanggapan…"
                            data-testid="input-visit-notes"
                          />
                        </div>
                        <div>
                          <Label>Pokok Doa (opsional)</Label>
                          <Textarea
                            rows={2}
                            value={form.prayer_notes}
                            onChange={(e) => setForm({ ...form, prayer_notes: e.target.value })}
                            placeholder="Pokok doa yang dibawakan bersama…"
                            data-testid="input-visit-prayer"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenVisit(false)} data-testid="btn-cancel-visit">Batal</Button>
                        <Button onClick={submitVisit} disabled={saving} data-testid="btn-save-visit">
                          {saving ? "Menyimpan…" : "Simpan Kunjungan"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {visitsLoading ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Memuat kunjungan…</div>
              ) : visits.length === 0 ? (
                <div className="border border-dashed border-border rounded-lg p-8 text-center" data-testid="pastoral-empty">
                  <HeartHandshake className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                  <div className="text-sm text-muted-foreground">Belum ada catatan kunjungan.</div>
                  {canCreateVisit && (
                    <div className="text-xs text-muted-foreground mt-1">Klik <strong>Catat Kunjungan</strong> untuk mulai.</div>
                  )}
                </div>
              ) : (
                <ul className="space-y-3" data-testid="pastoral-list">
                  {visits.map((v) => {
                    const meta = condMeta(v.condition);
                    const canDelete = canDeleteAny || v.visited_by_id === user?.id;
                    return (
                      <li key={v.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition" data-testid={`visit-item-${v.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center shrink-0">
                              <Calendar className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="font-medium text-sm">{fmtDate(v.visit_date)}</div>
                              <div className="text-[0.7rem] text-muted-foreground mt-0.5">
                                oleh <strong>{v.visited_by || "—"}</strong>
                                {v.photo_count > 0 && (
                                  <span className="ml-2 inline-flex items-center gap-1"><CameraIcon className="h-3 w-3" /> {v.photo_count} foto</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={meta.tone} label={meta.label} />
                            {canDelete && (
                              <button
                                onClick={() => removeVisit(v.id)}
                                className="text-rose-600 hover:text-rose-800 p-1"
                                title="Hapus"
                                data-testid={`btn-del-visit-${v.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        {(v.notes || v.prayer_notes) && (
                          <div className="mt-3 pl-13 space-y-2 text-xs">
                            {v.notes && (
                              <div>
                                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold mb-0.5">Catatan</div>
                                <div className="text-foreground whitespace-pre-wrap">{v.notes}</div>
                              </div>
                            )}
                            {v.prayer_notes && (
                              <div>
                                <div className="text-[0.65rem] uppercase text-muted-foreground tracking-widest font-semibold mb-0.5">Pokok Doa</div>
                                <div className="text-foreground whitespace-pre-wrap italic">{v.prayer_notes}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
